import {DecodedJWT} from '@practicegenius/http-clients-js/src/types';
import * as R from 'ramda';
import {UserType} from './BaseUser';

export type UserAuth = {
  userId: number;
  userType: UserType;
  practiceId: number;
  timezone: string;
};

const decodeIncomingJwt = (encoded: string) => {
  if (typeof window !== 'undefined') {
    // Browser environment
    return atob(encoded);
  }
  // Node.js environment
  return Buffer.from(encoded, 'base64').toString();
};

/**
 * Given a JWT, return the decoded payload.
 */
export function decodeJwt(jwt: string): DecodedJWT {
  const segments = jwt.split('.');

  if (segments.length !== 3)
    throw new Error(
      "JWT is malformed. It must be of the form 'header.payload.signature'"
    );

  try {
    const payload = segments[1];
    return JSON.parse(decodeIncomingJwt(payload));
  } catch (err) {
    throw new Error(
      `JWT payload could not be parsed - ${(err as Error).message}`
    );
  }
}

/**
 * Given a decoded JWT payload, return a usable auth object.
 * @param token
 * @return {{userId: int, userRole: string, practiceId: int, timezone: string}}
 */
export function tokenToAuth(token: DecodedJWT): UserAuth {
  return {
    userId: token.sub,
    userType: token.subtype as UserAuth['userType'],
    practiceId: token.pid,
    timezone: token.zoneinfo,
  };
}

/**
 * Convert a JWT to UserAuth in one step
 */
export const jwtToAuth: (jwt: string) => UserAuth = R.pipe(
  decodeJwt,
  tokenToAuth
);
